import { useCallback, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import {
  MdArrowBack,
  MdLogin,
  MdOutlineEmail,
  MdPushPin,
  MdCheckCircleOutline,
  MdTrendingUp,
  MdTrendingDown,
  MdStar,
  MdOutlineFileDownload,
  MdOutlineEdit,
  MdOutlineBusinessCenter,
  MdOutlineCelebration,
  MdOutlineFlag,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { APP_LOGIN_URL, CLIENTS, CRM, PROFESSIONALS, STAFF } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import useCrmAccount from "../components/crm/useCrmAccount";
import AccountCharts from "../components/crm/AccountCharts";
import BillingPanel from "../components/crm/BillingPanel";
import SubscriptionPanel from "../components/subscriptions/SubscriptionPanel";
import ClientSuggestions from "../components/crm/ClientSuggestions";
import Modal from "../components/modals/Modal";
import CompanySupportForm from "../components/forms/CompanySupportForm";
import ContactMenu from "../components/crm/account/ContactMenu";
import OutcomePrompt from "../components/crm/account/OutcomePrompt";
import NextActionCard from "../components/crm/account/NextActionCard";
import TagPicker from "../components/crm/account/TagPicker";
import HealthPanel, { HealthBadge } from "../components/crm/account/HealthPanel";
import AdoptionPanel from "../components/crm/account/AdoptionPanel";
import ProfilePanel from "../components/crm/account/ProfilePanel";
import AccountTimeline from "../components/crm/account/AccountTimeline";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
} from "../components/crm/Primitives";
import {
  SkeletonHeader,
  SkeletonKpiStrip,
  SkeletonChart,
  SkeletonPanel,
  SkeletonWorkList,
} from "../components/crm/Skeletons";
import {
  formatDate,
  formatDaysCount,
  formatMoney,
  formatNumber,
  formatRelativeDays,
  initialsOf,
  INTERACTION_OUTCOMES,
  INTERACTION_TYPES,
  PLAN_TONE,
  PRIORITY_OPTIONS,
} from "../components/crm/crmHelpers";

/*
  Ficha da conta.

  Era uma página só, de cima a baixo: para registrar uma ligação olhando os números de
  uso, era preciso rolar a tela inteira várias vezes, e o que importava para decidir o
  que fazer (plano, valor, saúde, próxima ação) ficava longe do que acontecia com a conta.

  Agora são três partes que a pessoa enxerga ao mesmo tempo:

  1. **Cabeçalho fixo** — quem é, quanto paga, como está, quem cuida e o que fazer.
  2. **Abas à esquerda** — para estudar a conta, um assunto por vez. A aba fica na URL
     (`?aba=`), então o link leva ao mesmo lugar e "voltar" não se perde.
  3. **Coluna à direita** — contato, tarefas e histórico, sempre à vista. É o que a
     pessoa faz enquanto lê o resto.

  No celular as três viram uma coluna só, e o cabeçalho deixa de ser fixo: ali ele
  ocuparia metade da tela.
*/

const ABAS = [
  { id: "geral", label: "Visão geral" },
  { id: "uso", label: "Uso" },
  { id: "financeiro", label: "Financeiro" },
  { id: "equipe", label: "Equipe" },
];

/*
  `permissionId` do profissional (tabela Permissions do app). O painel mostrava
  o código cru, e "USER_CALENDARS" não diz a ninguém que aquela pessoa vê a
  agenda de toda a equipe.
*/
const ROTULO_PAPEL = {
  ADMIN: "Administrador",
  POWER_USER: "Acesso total",
  USER_CALENDARS: "Vê todas as agendas",
  USER: "Profissional",
};

const ROTULO_CANAL = {
  instagram: "Instagram",
  tiktok: "TikTok",
  indicacao: "Indicação",
  google: "Google",
  loja: "Loja de apps",
  outro: "Outro",
};

// De onde veio a mensalidade: o valor da assinatura é o combinado; o de tabela, o esperado.
const ROTULO_ORIGEM_VALOR = {
  SUBSCRIPTION: "valor da assinatura",
  TABLE: "preço de tabela",
};

/* -------------------------------------------------------------- indicadores */

/*
  Indicador com variação.

  O `delta` é sempre relativo ao período anterior equivalente. Sem ele, um
  número isolado não se lê: 89 agendamentos é bom para um salão de uma cadeira
  e ruim para um de quatro, mas "18% abaixo do mês passado" é ruim para
  qualquer um.
*/
const Kpi = ({ label, value, hint, delta, tone = "neutral", icon: Icon }) => {
  const subindo = delta > 0;
  const Seta = subindo ? MdTrendingUp : MdTrendingDown;

  return (
    <div className="bg-white px-4 py-3">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </p>
      <p className="mt-1 flex items-baseline gap-2">
        <span className="text-lg font-bold leading-none text-slate-900">
          {value}
        </span>
        {delta !== undefined && delta !== null && Number.isFinite(delta) ? (
          <span
            className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
              subindo ? "text-emerald-600" : "text-rose-600"
            }`}
          >
            <Seta size={13} />
            {Math.abs(Math.round(delta))}%
          </span>
        ) : null}
      </p>
      {hint ? (
        <p className={`mt-0.5 text-[11px] ${tone === "danger" ? "text-rose-600" : "text-slate-500"}`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};

const Dado = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="shrink-0 text-xs text-slate-500">{label}</span>
    <span className="truncate text-right text-xs font-semibold text-slate-800">
      {value || "—"}
    </span>
  </div>
);

/* ---------------------------------------------------------------- a página */

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, isAdmin } = useApplication();
  const [searchParams, setSearchParams] = useSearchParams();

  const abaPedida = searchParams.get("aba");
  const aba = ABAS.some((item) => item.id === abaPedida) ? abaPedida : "geral";

  // `replace`: trocar de aba não é navegar. "Voltar" sai da ficha, como antes.
  const trocarAba = (novaAba) =>
    setSearchParams(novaAba === "geral" ? {} : { aba: novaAba }, { replace: true });

  /*
    Voltar para a tela ANTERIOR, não para um destino fixo.

    O botão apontava para /pipeline sempre — então quem chegava pela tabela de
    clientes, por uma fila ou pelo relatório de uma campanha era jogado no
    pipeline e perdia o filtro, a página e a posição da lista.

    `location.key === "default"` significa que esta é a primeira entrada do
    histórico (link colado, aba nova): aí não há para onde voltar e o destino
    é a lista de clientes.
  */
  const voltar = () =>
    location.key === "default" ? navigate("/clientes") : navigate(-1);

  const [noteType, setNoteType] = useState("CALL");
  const [noteBody, setNoteBody] = useState("");
  const [noteOutcome, setNoteOutcome] = useState("");
  const [criandoTarefa, setCriandoTarefa] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDueDate, setTaskDueDate] = useState("");
  const [taskPriority, setTaskPriority] = useState("NORMAL");
  const [taskAssignee, setTaskAssignee] = useState("");
  const [exportando, setExportando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [personificando, setPersonificando] = useState(null);
  const [erroPersonificar, setErroPersonificar] = useState("");
  const [erroEtiqueta, setErroEtiqueta] = useState("");

  /*
    A coluna da direita fica fixa logo abaixo do cabeçalho, e a altura dele muda com o
    conteúdo (etiquetas, próxima ação). Medir é o que impede uma cobrir a outra.

    As contas de `top` e `max-h` levam em conta o `pt-4`/`pb-10` do contêiner da página,
    que o `sticky` respeita. O `-top-4` do cabeçalho o encosta na barra de cima: sem ele,
    a página rolando apareceria na fresta de 1rem acima dele. E sem o desconto na altura
    da coluna, ela subiria por baixo do cabeçalho no fim da rolagem.
  */
  const [alturaCabecalho, setAlturaCabecalho] = useState(0);
  const observadorCabecalho = useRef(null);
  const cabecalhoRef = useCallback((elemento) => {
    observadorCabecalho.current?.disconnect();
    if (!elemento) return;

    setAlturaCabecalho(elemento.offsetHeight);

    if (typeof ResizeObserver === "undefined") return;
    observadorCabecalho.current = new ResizeObserver(() =>
      setAlturaCabecalho(elemento.offsetHeight)
    );
    observadorCabecalho.current.observe(elemento);
  }, []);

  /*
    Entrar no app como a profissional, para ver o que ela vê.

    POST numa rota própria (não o GET do cadastro): a chamada cria a sessão e
    registra em SupportAuditLog quem abriu a conta de quem. Só ADMIN — para
    gerente de contas o servidor responde 403 e a mensagem dele aparece aqui.

    Aba nova para não derrubar a sessão do painel: o token do salão e o do
    staff moram na MESMA chave `token` do localStorage, em origens diferentes
    em produção. Navegar na mesma aba sobrescreveria um com o outro.
  */
  const entrarComo = async (profissional) => {
    setErroPersonificar("");
    setPersonificando(profissional.id);

    try {
      const { data: resposta } = await axios.post(
        `${PROFESSIONALS.IMPERSONATE}/${profissional.id}/impersonate`
      );

      window.open(
        `${APP_LOGIN_URL}?token=${resposta.data.token}`,
        "_blank",
        "noopener"
      );
      // A entrada agora aparece no histórico da conta.
      queryClient.invalidateQueries(["crm", "client", String(id)]);
    } catch (erro) {
      setErroPersonificar(
        erro.response?.data?.message ||
          "Não foi possível abrir a sessão de suporte."
      );
    } finally {
      setPersonificando(null);
    }
  };

  /*
    Exportação da base de clientes finais do salão. Estava no modal de
    detalhes da tabela, que saiu quando o olho passou a abrir esta página —
    a ação precisava vir junto, senão sumia sem substituto.
  */
  const exportarClientes = async () => {
    setExportando(true);
    try {
      const resposta = await axios.get(
        `${CLIENTS.EXPORT_CUSTOMERS}/${id}/customers/export`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([resposta.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `clientes-${id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExportando(false);
    }
  };

  const {
    relationship,
    guidance,
    account: data,
    usage,
    error: actionError,
    addInteraction,
    setOutcome,
    addTask,
    setTaskStatus,
    togglePin,
    removeInteraction,
    stageLabel,
  } = useCrmAccount(id);

  /*
    A ficha completa vem de GET /clients/:id — série de uso, histórico de
    pagamento, avaliações, catálogo. Este endpoint já existia e devolvia tudo
    isso; a tela anterior usava a LISTAGEM filtrada por #id e jogava fora o
    resto.
  */
  const detail = useQuery(["client", "detail", id], async () => {
    const { data: response } = await axios.get(`${CLIENTS.ONE}/${id}`);
    return response.data;
  });

  // Fica sob a chave da conta: qualquer ação nela relê também este bloco.
  const insights = useQuery(
    ["crm", "client", String(id), "insights"],
    async () => {
      const { data: response } = await axios.get(
        `${CRM.CLIENT_INSIGHTS}/${id}/insights`
      );
      return response.data;
    },
    { retry: false }
  );

  const { data: staffOptions } = useQuery(
    ["staff", "options"],
    async () => {
      const { data: response } = await axios.get(STAFF.OPTIONS);
      return response.data;
    },
    { enabled: isAdmin }
  );

  if (relationship.isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonHeader />
        <SkeletonKpiStrip count={6} withIcon={false} />
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <SkeletonChart height={240} bars={14} className="xl:col-span-2" />
          <div className="space-y-4">
            <SkeletonPanel lines={7} />
            <SkeletonPanel lines={5} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <SkeletonPanel lines={4} />
          <SkeletonWorkList rows={4} withHeader={false} />
        </div>
      </div>
    );
  }

  if (relationship.error) {
    return (
      <ErrorBlock
        message={
          relationship.error.response?.data?.message ||
          "Não foi possível carregar a ficha deste cliente."
        }
        onRetry={relationship.refetch}
      />
    );
  }

  const company = data.company;
  const info = detail.data;
  const ficha = info?.company || {};

  /*
    A equipe já vinha no payload de GET /clients/:id (getCompanyDetails) e
    nunca era exibida. Vem ordenada pelo servidor — excluídos por último,
    ativos primeiro, depois por nome — então a ordem não é refeita aqui.
  */
  const profissionais = info?.professionals || [];
  const ativos = profissionais.filter(
    (pessoa) => pessoa.isActive && !pessoa.isExcluded
  ).length;
  const lifetime = info?.lifetimeSeries || [];
  const uso = info?.usageSeries || [];
  const telefone = usage?.phoneNumber || ficha.phoneNumber;

  const ultimoAcessoPorPessoa = new Map(
    (insights.data?.teamAccess || []).map((item) => [
      Number(item.professionalId),
      item.lastSeenAt,
    ])
  );

  /*
    Tendência entre os dois últimos meses FECHADOS.

    Comparar o mês corrente com o anterior é a armadilha clássica do painel:
    no dia 4, outubro tem 4 dias de dados contra os 30 de setembro, e a conta
    mais saudável da base aparece com "-92%". O mês em andamento (o último da
    série) é descartado por isso.
  */
  const tendencia = (() => {
    if (lifetime.length < 3) return null;

    const fechado = lifetime[lifetime.length - 2];
    const anterior = lifetime[lifetime.length - 3];
    if (!anterior?.appointments) return null;

    return {
      mes: fechado.label,
      total: fechado.appointments,
      variacao:
        ((fechado.appointments - anterior.appointments) / anterior.appointments) *
        100,
    };
  })();

  const canDelete = (item) =>
    isAdmin || Number(item.authorId) === Number(user?.id);

  const proximaAcao = guidance.data?.primary;
  const totalAvisos = guidance.data?.alerts?.length || 0;

  return (
    <div
      className="space-y-4"
      style={{ "--ficha-top": `${alturaCabecalho}px` }}
    >
      {/* ------------------------------------------------- cabeçalho fixo */}
      <header
        ref={cabecalhoRef}
        data-testid="account-header"
        className="z-20 rounded-2xl border border-slate-200 bg-white shadow-sm lg:sticky lg:-top-4"
      >
        <div className="flex flex-wrap items-start gap-3 px-4 py-3">
          <button
            type="button"
            onClick={voltar}
            className="mt-1 shrink-0 text-slate-400 transition hover:text-pink-600"
            title="Voltar para a tela anterior"
          >
            <MdArrowBack size={20} />
          </button>

          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-pink-600 text-sm font-bold text-white shadow-sm">
            {initialsOf(company.name)}
          </span>

          <div className="min-w-0 flex-1 basis-40">
            <h1 className="truncate text-lg font-bold leading-tight text-slate-900">
              {company.name}
            </h1>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
              <span className="font-mono">#{company.id}</span>
              {ficha.documentId ? <span>· {ficha.documentId}</span> : null}
              {ficha.acquisitionSource ? (
                <span>· veio do {ROTULO_CANAL[ficha.acquisitionSource] || ficha.acquisitionSource}</span>
              ) : null}
              {ficha.createdAt ? (
                <span>
                  ·{" "}
                  {usage?.daysSinceRegistration === 0
                    ? "cliente desde hoje"
                    : `cliente há ${formatDaysCount(usage?.daysSinceRegistration)}`}
                </span>
              ) : null}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ContactMenu
              account={{ name: company.name, ...usage }}
              phoneNumber={telefone}
              guidance={guidance.data}
              onRegister={(payload) => addInteraction.mutate(payload)}
            />
            {(usage?.email || ficha.email) ? (
              <a
                href={`mailto:${usage?.email || ficha.email}`}
                title="Escrever e-mail"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200"
              >
                <MdOutlineEmail size={16} /> E-mail
              </a>
            ) : null}
            {isAdmin ? (
              <button
                type="button"
                onClick={() => setEditando(true)}
                title="Editar o cadastro do salão"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200"
              >
                <MdOutlineEdit size={16} /> Editar
              </button>
            ) : null}
            {isAdmin ? (
              <button
                type="button"
                onClick={exportarClientes}
                disabled={exportando}
                title="Baixar a base de clientes deste salão em CSV"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200 disabled:opacity-40"
              >
                <MdOutlineFileDownload size={16} />
                {exportando ? "Exportando..." : "Clientes"}
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-slate-100 px-4 py-2 text-xs text-slate-600">
          {usage ? (
            <>
              <Badge tone={PLAN_TONE[usage.planId] || "neutral"}>{usage.planId}</Badge>
              <span
                className="font-semibold text-slate-900"
                title={ROTULO_ORIGEM_VALOR[usage.monthlyValueSource]}
              >
                {formatMoney(usage.monthlyValue)}/mês
              </span>
              <HealthBadge
                score={usage.healthScore}
                band={usage.healthBand}
                title={(usage.healthReasons || []).join(" · ")}
              />
              <Badge tone={usage.stageTone}>{usage.stageLabel}</Badge>
              <span>
                {usage.ownerName ? `carteira de ${usage.ownerName}` : "sem responsável"}
              </span>
            </>
          ) : null}
          {/* Evento de onde veio. A tela do evento é só do ADMIN; o gerente
              de contas vê o nome, sem o atalho. */}
          {data.event ? (
            isAdmin ? (
              <Link
                to={`/eventos/${data.event.id}`}
                title="Abrir o evento"
                className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2.5 py-0.5 font-semibold text-pink-700 ring-1 ring-inset ring-pink-200 hover:bg-pink-100"
              >
                <MdOutlineCelebration size={13} /> Veio pelo evento {data.event.name}
              </Link>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2.5 py-0.5 font-semibold text-pink-700 ring-1 ring-inset ring-pink-200">
                <MdOutlineCelebration size={13} /> Veio pelo evento {data.event.name}
              </span>
            )
          ) : null}
          <TagPicker
            companyId={id}
            tags={usage?.tags || []}
            onError={setErroEtiqueta}
          />
        </div>

        {proximaAcao || totalAvisos ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-b-2xl border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs">
            {proximaAcao ? (
              <span className="flex min-w-0 items-center gap-1.5 text-slate-800">
                <MdOutlineFlag size={14} className="shrink-0 text-pink-600" />
                <span className="font-semibold">{proximaAcao.action}</span>
                {proximaAcao.snoozedBy ? (
                  <span className="shrink-0 text-slate-500">· aguardando</span>
                ) : null}
              </span>
            ) : null}
            {totalAvisos ? (
              <button
                type="button"
                onClick={() => trocarAba("geral")}
                className="font-semibold text-rose-700 hover:underline"
              >
                {totalAvisos === 1 ? "1 aviso" : `${totalAvisos} avisos`}
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      {actionError ? <ErrorBlock message={actionError} /> : null}
      {erroEtiqueta ? <ErrorBlock message={erroEtiqueta} /> : null}

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        {/* ---------------------------------------------- abas (estudar a conta) */}
        <div className="min-w-0 space-y-4">
          <nav
            aria-label="Assuntos da conta"
            className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1"
          >
            {ABAS.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-current={aba === item.id ? "page" : undefined}
                onClick={() => trocarAba(item.id)}
                className={`shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                  aba === item.id
                    ? "bg-pink-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {aba === "geral" ? (
            <>
              <NextActionCard guidance={guidance.data} isLoading={guidance.isLoading} />

              {data.pinned.length > 0 ? (
                <Card className="border-l-4 border-l-pink-500 p-4">
                  <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-pink-700">
                    <MdPushPin size={13} /> Leia antes de falar com ela
                  </p>
                  <ul className="space-y-1.5">
                    {data.pinned.map((note) => (
                      <li key={note.id} className="text-sm text-slate-800">
                        <span className="whitespace-pre-wrap">{note.body}</span>
                        <span className="ml-1 text-[11px] text-slate-500">
                          — {note.authorName}, {formatDate(note.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ) : null}

              <Card className="overflow-hidden">
                <div className="grid grid-cols-2 gap-px bg-slate-100 md:grid-cols-3">
                  <Kpi
                    label="Mensalidade"
                    value={formatMoney(usage?.monthlyValue || 0)}
                    hint={
                      ROTULO_ORIGEM_VALOR[usage?.monthlyValueSource] ||
                      "sem plano pago"
                    }
                  />
                  <Kpi
                    label="Total pago"
                    value={formatMoney(usage?.totalPaid || 0)}
                    hint={
                      usage?.lastPaymentAt
                        ? `último em ${formatDate(usage.lastPaymentAt)}`
                        : "nenhum pagamento"
                    }
                  />
                  <Kpi
                    label="Vencimento"
                    value={
                      usage?.planExpirationDate ? formatDate(usage.planExpirationDate) : "—"
                    }
                    hint={
                      usage?.planExpirationDate
                        ? formatRelativeDays(usage.planExpirationDate)
                        : "—"
                    }
                    tone={usage?.daysToExpire <= 7 ? "danger" : "neutral"}
                  />
                  <Kpi
                    label="Agend. 30 dias"
                    value={formatNumber(usage?.appointmentsLast30Days || 0)}
                    delta={usage?.usageDropPercent ? -usage.usageDropPercent : undefined}
                    hint={`${formatNumber(usage?.appointmentsPrev30Days || 0)} nos 30 dias anteriores`}
                  />
                  <Kpi
                    label="Último acesso"
                    value={
                      usage?.lastAccessAt ? formatRelativeDays(usage.lastAccessAt) : "—"
                    }
                    hint={usage?.lastAccessAt ? "abriu o app" : "sem registro ainda"}
                    tone={usage?.daysSinceLastAccess > 14 ? "danger" : "neutral"}
                  />
                  <Kpi
                    label="Último contato"
                    value={
                      data.lastContactAt ? formatRelativeDays(data.lastContactAt) : "nunca"
                    }
                    hint={`${formatNumber(data.contactCount)} registro(s)`}
                    tone={data.lastContactAt ? "neutral" : "danger"}
                  />
                  <Kpi
                    label="Base"
                    value={`${formatNumber(usage?.activeProfessionalCount || 0)} / ${formatNumber(
                      info?.customersCount ?? usage?.customerCount ?? 0
                    )}`}
                    hint="profissionais ativos / clientes finais"
                  />
                  <Kpi
                    label="Avaliação"
                    icon={MdStar}
                    value={
                      info?.reviews?.total
                        ? info.reviews.average.toFixed(1).replace(".", ",")
                        : "—"
                    }
                    hint={
                      info?.reviews?.total
                        ? `${formatNumber(info.reviews.total)} avaliações`
                        : "sem avaliações"
                    }
                  />
                  <Kpi
                    label="Agend. no total"
                    value={formatNumber(usage?.appointmentCount || 0)}
                    hint="desde o cadastro"
                  />
                </div>
              </Card>

              <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                <div className="space-y-4">
                  <HealthPanel usage={usage} />
                  <Card className="p-4">
                    <h2 className="mb-2 text-sm font-bold text-slate-900">Dados da conta</h2>
                    <div className="divide-y divide-slate-100">
                      <Dado label="E-mail" value={usage?.email || ficha.email} />
                      <Dado label="Telefone" value={telefone} />
                      <Dado label="Documento" value={ficha.documentId} />
                      <Dado label="Cadastro" value={formatDate(ficha.createdAt)} />
                      <Dado label="Cupom" value={ficha.cupom} />
                      <Dado label="Plano" value={ficha.planId} />
                      {/*
                        Periodicidade contratada. O rótulo vem do servidor
                        (`billingCycleLabel`, montado a partir de config/planCatalog),
                        e não de um mapa local: o painel dizendo "Semestral" e a
                        cobrança chamando o mesmo ciclo de outra coisa é divergência
                        que o catálogo único existe para impedir.

                        Vazio quando a conta nunca contratou nada — ou pagou antes de
                        a coluna existir e o backfill não achou o ciclo no histórico.
                      */}
                      <Dado label="Periodicidade" value={ficha.billingCycleLabel} />
                      <Dado label="Vencimento do plano" value={formatDate(ficha.planExpirationDate)} />
                      <Dado
                        label="Limite de usuários"
                        value={ficha.numberOfUsers ? `${ficha.numberOfUsers} assento(s)` : null}
                      />
                    </div>
                  </Card>
                </div>

                <ProfilePanel insights={insights} />
              </div>

              <ClientSuggestions companyId={id} />
            </>
          ) : null}

          {aba === "uso" ? (
            <>
              <Card className="overflow-hidden">
                <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">Uso ao longo do tempo</h2>
                    <p className="text-[11px] text-slate-500">
                      Agendamentos e comandas por mês desde o cadastro
                    </p>
                  </div>

                  {tendencia ? (
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">
                        {tendencia.mes} fechou em {formatNumber(tendencia.total)}
                      </span>
                      <span
                        className={`inline-flex items-center gap-0.5 font-semibold ${
                          tendencia.variacao >= 0 ? "text-emerald-600" : "text-rose-600"
                        }`}
                      >
                        {tendencia.variacao >= 0 ? (
                          <MdTrendingUp size={14} />
                        ) : (
                          <MdTrendingDown size={14} />
                        )}
                        {Math.abs(Math.round(tendencia.variacao))}%
                      </span>
                      <span>vs. mês anterior</span>
                    </p>
                  ) : null}
                </div>

                {detail.isLoading ? (
                  <SkeletonChart height={240} bars={14} withHeader={false} />
                ) : (
                  <AccountCharts lifetimeSeries={lifetime} usageSeries={uso} />
                )}
              </Card>

              <AdoptionPanel insights={insights} />

              <Card className="p-4">
                <h2 className="mb-2 text-sm font-bold text-slate-900">Catálogo</h2>
                <div className="divide-y divide-slate-100">
                  <Dado label="Serviços" value={formatNumber(info?.servicesCount || 0)} />
                  <Dado label="Produtos" value={formatNumber(info?.productsCount || 0)} />
                  <Dado label="Categorias" value={formatNumber(info?.categoriesCount || 0)} />
                  {/*
                    `commandsCount` do endpoint vem AGRUPADO por status
                    ([{statusId, count}, ...]), não como total — renderizá-lo como
                    número dava 0 ao lado de um gráfico mostrando centenas. O total
                    limpo é `metrics.commandCount`, o mesmo que alimenta a tabela
                    de clientes.
                  */}
                  <Dado
                    label="Comandas"
                    value={formatNumber(info?.metrics?.commandCount || 0)}
                  />
                  <Dado label="Fichas de cliente" value={formatNumber(info?.recordsCount || 0)} />
                </div>
              </Card>
            </>
          ) : null}

          {aba === "financeiro" ? (
            <>
              <SubscriptionPanel
                company={{
                  id,
                  name: company.name,
                  phoneNumber: telefone,
                }}
              />

              <BillingPanel
                companyId={id}
                paymentHistory={info?.paymentHistory || []}
                summary={info?.paymentHistorySummary}
              />
            </>
          ) : null}

          {aba === "equipe" ? (
            <Card className="overflow-hidden">
              <div className="flex flex-wrap items-end justify-between gap-2 border-b border-slate-100 px-4 py-3">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Equipe</h2>
                  <p className="text-[11px] text-slate-500">
                    {profissionais.length} cadastrado(s) · {ativos} ativo(s)
                    {ficha.numberOfUsers
                      ? ` · ${ficha.numberOfUsers} assento(s) contratado(s)`
                      : ""}
                  </p>
                </div>

                {isAdmin ? (
                  <p className="text-[11px] text-slate-400">
                    Entrar como abre o app do salão em aba nova e vale 1 hora · fica
                    registrado na auditoria e no histórico
                  </p>
                ) : null}
              </div>

              {erroPersonificar ? (
                <div className="px-4 pt-3">
                  <ErrorBlock message={erroPersonificar} />
                </div>
              ) : null}

              {profissionais.length ? (
                <ul className="divide-y divide-slate-100">
                  {profissionais.map((pessoa) => {
                    /*
                      O app relê o profissional do banco a cada request e recusa
                      inativo ou excluído (ver authenticateJWT). Personificar um
                      deles abriria uma aba que cai no login no primeiro clique —
                      então o botão sai de cena e a tela diz por quê.
                    */
                    const podeEntrar = pessoa.isActive && !pessoa.isExcluded;
                    const entrando = personificando === pessoa.id;
                    const ultimoAcesso = ultimoAcessoPorPessoa.get(Number(pessoa.id));

                    return (
                      <li
                        key={pessoa.id}
                        className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
                          pessoa.isExcluded ? "opacity-60" : ""
                        }`}
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-[11px] font-bold text-pink-700">
                          {initialsOf(pessoa.name)}
                        </span>

                        <div className="min-w-0 flex-1 basis-40">
                          <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-slate-900">
                            <span className="truncate">{pessoa.name}</span>
                            <Badge
                              tone={pessoa.permissionId === "USER" ? "neutral" : "info"}
                            >
                              {ROTULO_PAPEL[pessoa.permissionId] || pessoa.permissionId}
                            </Badge>
                            {pessoa.isExcluded ? (
                              <Badge tone="danger">excluído</Badge>
                            ) : !pessoa.isActive ? (
                              <Badge tone="warning">desativado</Badge>
                            ) : null}
                          </p>
                          <p className="truncate text-[11px] text-slate-500">
                            {[pessoa.email, pessoa.phoneNumber]
                              .filter(Boolean)
                              .join(" · ") || "sem contato cadastrado"}
                          </p>
                        </div>

                        <span className="shrink-0 text-right text-[11px] text-slate-400">
                          <span className="block text-slate-600">
                            {ultimoAcesso
                              ? `abriu o app ${formatRelativeDays(ultimoAcesso)}`
                              : "sem registro de acesso"}
                          </span>
                          desde {formatDate(pessoa.createdAt)}
                        </span>

                        {isAdmin ? (
                          podeEntrar ? (
                            <button
                              type="button"
                              onClick={() => entrarComo(pessoa)}
                              disabled={entrando}
                              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <MdLogin size={14} />
                              {entrando ? "Abrindo..." : "Entrar como"}
                            </button>
                          ) : (
                            <span className="shrink-0 text-[11px] text-slate-400">
                              sem acesso ao app
                            </span>
                          )
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <EmptyState
                  icon={MdOutlineBusinessCenter}
                  title="Nenhum profissional cadastrado"
                  description="A conta existe mas ninguém da equipe foi cadastrado ainda — é o caso clássico de conta que nunca saiu do papel."
                />
              )}
            </Card>
          ) : null}
        </div>

        {/* ------------------------------------ contato, tarefas e histórico */}
        {/* <section>, e não <aside>: o CSS global do menu lateral pinta `aside > div`. */}
        <section
          aria-label="Contato, tarefas e histórico"
          data-testid="account-side"
          className="min-w-0 space-y-4 xl:sticky xl:top-[var(--ficha-top)] xl:max-h-[calc(100vh-65px-var(--ficha-top)-3.5rem)] xl:overflow-y-auto xl:pr-1"
        >
          <Card className="p-4">
            <h2 className="text-sm font-bold text-slate-900">Registrar contato</h2>
            <p className="mb-2 text-[11px] text-slate-500">
              O que foi dito fica com a conta, não no WhatsApp de quem atendeu
            </p>

            <div className="flex flex-wrap gap-1.5">
              {INTERACTION_TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setNoteType(option.value)}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset transition ${
                    noteType === option.value
                      ? "bg-pink-600 text-white ring-pink-600"
                      : "bg-white text-slate-600 ring-slate-200 hover:ring-pink-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <textarea
              value={noteBody}
              onChange={(event) => setNoteBody(event.target.value)}
              rows={2}
              maxLength={5000}
              placeholder="O que ela disse, o que ficou combinado..."
              className="mt-2 w-full resize-y rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            />

            {/* Anotação não é contato: não há o que perguntar. */}
            {noteType !== "NOTE" ? (
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-slate-500">Resultado:</span>
                {INTERACTION_OUTCOMES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={noteOutcome === option.value}
                    onClick={() =>
                      setNoteOutcome((atual) =>
                        atual === option.value ? "" : option.value
                      )
                    }
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset transition ${
                      noteOutcome === option.value
                        ? "bg-slate-800 text-white ring-slate-800"
                        : "bg-white text-slate-600 ring-slate-200 hover:ring-pink-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}

            <button
              type="button"
              onClick={() =>
                addInteraction.mutate(
                  {
                    type: noteType,
                    body: noteBody,
                    outcome:
                      noteType !== "NOTE" && noteOutcome ? noteOutcome : undefined,
                  },
                  {
                    onSuccess: () => {
                      setNoteBody("");
                      setNoteOutcome("");
                    },
                  }
                )
              }
              disabled={!noteBody.trim() || addInteraction.isLoading}
              className="mt-2 w-full rounded-xl bg-pink-600 py-2 text-sm font-semibold text-white transition hover:bg-pink-700 disabled:opacity-40"
            >
              {addInteraction.isLoading ? "Salvando..." : "Registrar"}
            </button>
          </Card>

          <Card className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="text-sm font-bold text-slate-900">Tarefas</h2>
                <p className="text-[11px] text-slate-500">
                  Compromisso com data e dono — tira a conta das filas automáticas
                </p>
              </div>
              {/* Fechado por padrão, para o histórico caber na mesma tela. */}
              <button
                type="button"
                aria-expanded={criandoTarefa}
                onClick={() => setCriandoTarefa((aberto) => !aberto)}
                className="shrink-0 rounded-lg border border-pink-200 px-2.5 py-1 text-xs font-semibold text-pink-700 transition hover:bg-pink-50"
              >
                {criandoTarefa ? "Fechar" : "Nova tarefa"}
              </button>
            </div>

            {data.openTasks.length > 0 ? (
              <ul className="mt-2 space-y-1.5">
                {data.openTasks.map((task) => (
                  <li
                    key={task.id}
                    className="flex items-start gap-2 rounded-xl bg-slate-50 p-2.5"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setTaskStatus.mutate({ taskId: task.id, status: "DONE" })
                      }
                      title="Marcar como feita"
                      className="mt-0.5 shrink-0 text-slate-300 transition hover:text-emerald-600"
                    >
                      <MdCheckCircleOutline size={18} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800">{task.title}</p>
                      <p className="text-[11px] text-slate-500">
                        {task.assigneeName}
                        {task.dueDate ? ` · vence ${formatRelativeDays(task.dueDate)}` : ""}
                      </p>
                    </div>
                    {task.isOverdue ? <Badge tone="danger">atrasada</Badge> : null}
                  </li>
                ))}
              </ul>
            ) : null}

            {criandoTarefa ? (
              <div className="mt-3">
                <input
                  value={taskTitle}
                  onChange={(event) => setTaskTitle(event.target.value)}
                  maxLength={180}
                  placeholder="Ex.: ligar para fechar o upgrade"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                />

                <div className="mt-2 grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={taskDueDate}
                    onChange={(event) => setTaskDueDate(event.target.value)}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300"
                  />
                  <select
                    value={taskPriority}
                    onChange={(event) => setTaskPriority(event.target.value)}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300"
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {isAdmin ? (
                  <select
                    value={taskAssignee}
                    onChange={(event) => setTaskAssignee(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300"
                  >
                    <option value="">Responsável: dono da carteira (ou eu)</option>
                    {(staffOptions || []).map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                ) : null}

                <button
                  type="button"
                  onClick={() =>
                    addTask.mutate(
                      {
                        title: taskTitle,
                        dueDate: taskDueDate || null,
                        priority: taskPriority,
                        assignedToUserId: taskAssignee || undefined,
                      },
                      {
                        onSuccess: () => {
                          setTaskTitle("");
                          setTaskDueDate("");
                          setTaskPriority("NORMAL");
                          setCriandoTarefa(false);
                        },
                      }
                    )
                  }
                  disabled={!taskTitle.trim() || addTask.isLoading}
                  className="mt-2 w-full rounded-xl border border-pink-200 py-2 text-sm font-semibold text-pink-700 transition hover:bg-pink-50 disabled:opacity-40"
                >
                  {addTask.isLoading ? "Criando..." : "Criar tarefa"}
                </button>
              </div>
            ) : null}
          </Card>

          <AccountTimeline
            timeline={data.timeline}
            stageLabel={stageLabel}
            canDelete={canDelete}
            onTogglePin={(interactionId, pinned) =>
              togglePin.mutate({ interactionId, pinned })
            }
            onDelete={(interactionId) => removeInteraction.mutate(interactionId)}
            onOutcome={(payload) => setOutcome.mutate(payload)}
          />
        </section>
      </div>

      <OutcomePrompt
        timeline={data.timeline}
        onAnswer={(payload) => setOutcome.mutate(payload)}
        isSaving={setOutcome.isLoading}
      />

      {/*
        Mesmo formulário usado na tabela de clientes. Ele busca os dados
        completos sozinho a partir do id, então reaproveitar aqui não exige
        carregar nada antes — e evita duas telas de edição que divergiriam na
        primeira mudança de campo.
      */}
      <Modal
        isOpen={editando}
        onClose={setEditando}
        size="lg"
        icon={MdOutlineBusinessCenter}
        title="Editar cliente"
        subtitle={`${company.name} · #${company.id}`}
        bodyClassName="bg-slate-50/50"
      >
        <CompanySupportForm
          company={company}
          type="EDIT"
          isDrawerOpen={editando}
          onClose={setEditando}
          owners={staffOptions || []}
          canAssignOwner={isAdmin}
          fetchCompanies={() => {
            queryClient.invalidateQueries(["client", "detail", String(id)]);
            queryClient.invalidateQueries(["crm", "client", String(id)]);
            queryClient.invalidateQueries(["companies"]);
          }}
        />
      </Modal>
    </div>
  );
};

export default ClientDetailPage;
